import React, { useMemo, useState, useEffect } from "react";
import { useCart } from "../../contexts/CartContext";
import { useTranslation } from "react-i18next";
import PlacesAutocomplete from "../../components/Places/PlacesAutocomplete";
import {
  TrashIcon,
  TruckIcon,
  XMarkIcon,
  MapPinIcon,
  ExclamationTriangleIcon,
  ArrowLeftIcon,
} from "@heroicons/react/24/outline";
import imagenotfound from "../../images/imagenotfound.jpg";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";

const currency = new Intl.NumberFormat("en-CA", {
  style: "currency",
  currency: "CAD",
});

const isShippableItem = (it) =>
  (it?.itemType === "product" && it?.requiresShipping !== false) ||
  it?.requiresShipping === true;

const isPendingItem = (it) =>
  it?.isWaitingForContact === true ||
  it?.proposeDates === true ||
  it?.isPending === true;

function groupCartIntoShipments(cart) {
  const shipItems = (cart || []).filter(isShippableItem);
  if (shipItems.length === 0) return [];
  const byWarehouse = new Map();
  for (const item of shipItems) {
    const keyRaw =
      (item.warehouseId != null ? String(item.warehouseId) : null) ??
      (item.companyId != null ? String(item.companyId) : null) ??
      `item-${item.productId ?? item.linkedItemId ?? item.id}`;
    if (!byWarehouse.has(keyRaw))
      byWarehouse.set(keyRaw, { id: keyRaw, items: [] });
    byWarehouse.get(keyRaw).items.push(item);
  }
  return Array.from(byWarehouse.values());
}

function getShipmentTitle(shipment, fallback) {
  const it = shipment.items?.[0] || {};
  const name =
    it.companyName ||
    it.supplierName ||
    it.vendorName ||
    it.sellerName ||
    it.warehouseName;
  const count = shipment.items?.length || 0;
  if (name) return `${name} — ${count} ${count > 1 ? "articles" : "article"}`;
  return `${fallback} — ${count} ${count > 1 ? "articles" : "article"}`;
}

async function fetchAllShippingRatesFromCart({ cart, address }) {
  const itemsForApi = Object.values(
    (cart || [])
      .filter(
        (it) => it?.itemType === "product" && it?.requiresShipping !== false
      )
      .reduce((acc, it) => {
        const productId =
          it.productId ?? (it.itemType === "product" ? it.linkedItemId : null);
        if (!productId) return acc;
        const key = String(productId);
        if (!acc[key]) {
          acc[key] = {
            productId,
            quantity: 0,
            companyId: it.companyId ?? null,
            warehouseId: it.warehouseId ?? null,
            preferredCarrier: it.carrier ?? null,
            weight: it.weight ?? null,
            dimensions: it.dimensions ?? null,
          };
        }
        acc[key].quantity += Number.isFinite(it.quantity) ? it.quantity : 1;
        return acc;
      }, {})
  );

  const resp = await fetch(
    `${process.env.REACT_APP_API_BASE_URL}/api/Store/ShippingRates/ForProducts`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        address: {
          formattedAddress: address?.formattedAddress,
          latitude: address?.latitude,
          longitude: address?.longitude,
          regionId: address?.regionId,
        },
        items: itemsForApi,
      }),
    }
  );
  if (!resp.ok) {
    const msg = await resp.text().catch(() => "");
    throw new Error(`Shipping error: ${resp.status} ${msg}`);
  }
  return await resp.json();
}

function RateBadge({ show, children, className = "" }) {
  if (!show) return null;
  return (
    <span
      className={`inline-block text-[10px] px-2 py-0.5 rounded-full ${className}`}
    >
      {children}
    </span>
  );
}

function formatEta(eta) {
  if (!eta) return "";
  const { minDays, maxDays } = eta;
  if (Number.isFinite(minDays) && Number.isFinite(maxDays))
    return `${minDays}–${maxDays + 1} j`;
  if (Number.isFinite(minDays)) return `${minDays + 1} j`;
  return "";
}

function ShippingOptionsModal({
  open,
  onClose,
  shipments,
  addressObj,
  initialSelected = {},
  onSave,
  cart,
}) {
  const { t } = useTranslation();
  const [serverShipments, setServerShipments] = useState([]);
  const [modalAddress, setModalAddress] = useState(addressObj || null);
  const [step, setStep] = useState(0);
  const [ratesMap, setRatesMap] = useState({});
  const [selected, setSelected] = useState(initialSelected);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const hasShipments = shipments.length > 0;
  const isLast = step >= shipments.length - 1;
  const current = hasShipments ? shipments[step] : null;
  const currentKey = current ? String(current.id) : null;
  const currentRates = currentKey ? ratesMap[currentKey] || [] : [];
  const currentSelected = currentKey ? selected[currentKey] : null;

  const shippingTotal = Object.values(selected).reduce(
    (sum, r) => sum + (r?.price || 0),
    0
  );

  async function loadAllRatesOnce(addr) {
    if (!addr?.formattedAddress) {
      setError("Veuillez entrer une adresse.");
      return;
    }
    try {
      setError("");
      setLoading(true);
      const data = await fetchAllShippingRatesFromCart({ cart, address: addr });
      setServerShipments(data.shipments || []);

      const localKeyForServer = (srv) => {
        const byWh = (shipments || []).find((sh) =>
          sh.items?.some(
            (it) =>
              it.warehouseId != null &&
              String(it.warehouseId) === String(srv.warehouseId)
          )
        );
        if (byWh) return String(byWh.id);

        const byCo = (shipments || []).find((sh) =>
          sh.items?.some(
            (it) =>
              it.companyId != null &&
              String(it.companyId) === String(srv.companyId)
          )
        );
        if (byCo) return String(byCo.id);

        const srvPids = new Set((srv.productIds || []).map((x) => String(x)));
        const byPid = (shipments || []).find((sh) =>
          sh.items?.some((it) => {
            const pid = it.productId ?? it.linkedItemId ?? it.id;
            return pid != null && srvPids.has(String(pid));
          })
        );
        if (byPid) return String(byPid.id);

        return String(srv.warehouseId ?? srv.companyId ?? Math.random());
      };

      const map = {};
      for (const s of data.shipments || []) {
        const k = localKeyForServer(s);
        map[k] = s.rates || [];
      }
      setRatesMap(map);

      setSelected((prev) => {
        const next = { ...prev };
        for (const s of data.shipments || []) {
          const k = localKeyForServer(s);
          const list = map[k] || [];
          const keep = list.find((r) => r.id === prev[k]?.id);
          const cheapest = list.find((r) => r.isCheapest) || list[0] || null;
          next[k] = keep || cheapest || null;
        }
        return next;
      });
    } catch (e) {
      console.error("shipping-rates error", e);
      setError("Impossible d'obtenir les tarifs.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!open) return;
    setModalAddress(addressObj || null);
    setStep(0);
    setRatesMap({});
    setServerShipments([]);
    setSelected(
      Object.keys(initialSelected || {}).length ? initialSelected : {}
    );
    loadAllRatesOnce(addressObj || modalAddress);
  }, [open, addressObj, initialSelected, shipments]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <div className="text-lg font-semibold">
              {t("cart.chooseShipping") || "Choisir la livraison"}
            </div>
            <button
              onClick={onClose}
              className="p-1 rounded hover:bg-gray-100"
              aria-label="Fermer"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          <div className="px-4 py-3 border-b bg-gray-50">
            <div className="flex items-start gap-3">
              <MapPinIcon className="w-5 h-5 text-gray-500 mt-1" />
              <div className="flex-1">
                <PlacesAutocomplete
                  initialAddress={modalAddress?.formattedAddress || ""}
                  onSelect={(coords) => {
                    const text = coords?.formattedAddress || "";
                    setModalAddress(text ? coords : null);
                    setStep(0);
                    setRatesMap({});
                    setSelected({});
                    const first = shipments[0];
                    if (first && text) {
                      loadAllRatesOnce(coords);
                    }
                  }}
                  showOptional={false}
                  title={t("cart.deliveryAddress")}
                  placeholder={
                    t("cart.enterAddress") || "Entrez une adresse au Québec"
                  }
                  customClassName=""
                  canEdit={true}
                />
                {error && (
                  <div className="mt-2 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-2 text-sm text-red-700">
                    <ExclamationTriangleIcon className="w-5 h-5 mt-0.5" />
                    <div>{error}</div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-auto p-4 space-y-4">
            {current && (
              <div className="rounded-xl border border-gray-200 p-3">
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <TruckIcon className="w-5 h-5 text-gray-500" />
                    <div className="font-medium">
                      {getShipmentTitle(
                        current,
                        `${t("cart.shipment") || "Envoi"} #${step + 1}`
                      )}
                    </div>
                  </div>
                  {shipments.length > 1 && (
                    <div className="text-xs text-gray-500">
                      {step + 1} / {shipments.length}
                    </div>
                  )}
                </div>

                <div className="mb-3 flex gap-2 overflow-x-auto">
                  {current.items.map((it, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-2 border rounded-lg px-2 py-1"
                    >
                      <img
                        src={it.imageUrl || imagenotfound}
                        alt={it.title || it.nameFr || it.nameEn}
                        className="w-10 h-10 rounded object-cover"
                        loading="lazy"
                      />
                      <div className="text-xs">
                        <div className="font-medium truncate max-w-[140px]">
                          {it.title || it.nameFr || it.nameEn}
                        </div>
                        <div className="text-gray-500">x{it.quantity || 1}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {loading && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {[0, 1, 2, 3].map((k) => (
                      <div
                        key={k}
                        className="h-20 rounded-xl bg-gray-100 animate-pulse"
                      />
                    ))}
                  </div>
                )}

                {!loading && currentRates.length === 0 && (
                  <div className="text-sm text-gray-500">
                    {t("cart.noShippingOptions") ||
                      "Aucune option disponible pour cet envoi."}
                  </div>
                )}

                {!loading && currentRates.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {currentRates.map((rate) => {
                      const selectedRow = currentSelected?.id === rate.id;
                      return (
                        <label
                          key={rate.id}
                          className={`flex items-start justify-between gap-3 rounded-xl border p-3 cursor-pointer ${
                            selectedRow
                              ? "border-blue-500 ring-2 ring-blue-200"
                              : "border-gray-200 hover:border-gray-300"
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <input
                              type="radio"
                              name={`rate-${currentKey}`}
                              checked={selectedRow}
                              onChange={() =>
                                setSelected((prev) => ({
                                  ...prev,
                                  [currentKey]: rate,
                                }))
                              }
                            />
                            <div className="flex flex-col">
                              <div className="text-sm font-medium">
                                {rate.carrier} — {rate.serviceName}
                              </div>
                              <div className="text-xs text-gray-500">
                                {formatEta(rate.eta)}
                              </div>
                              <div className="mt-1 flex gap-2">
                                <RateBadge
                                  show={rate.isCheapest}
                                  className="bg-emerald-100 text-emerald-700"
                                >
                                  {t("cart.cheapest") || "Le moins cher"}
                                </RateBadge>
                                <RateBadge
                                  show={rate.isFastest}
                                  className="bg-purple-100 text-purple-700"
                                >
                                  {t("cart.fastest") || "Le plus rapide"}
                                </RateBadge>
                              </div>
                            </div>
                          </div>
                          <div className="text-sm font-semibold">
                            {currency.format(rate.price)}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="border-t px-4 py-3 flex items-center justify-between bg-white">
            <div className="text-sm">
              {t("cart.shipping") || "Livraison"}:{" "}
              <span className="font-semibold">
                {currency.format(shippingTotal)}
              </span>
            </div>
            <div className="flex gap-2">
              <button
                className="px-4 py-2 rounded-full border hover:bg-gray-50"
                onClick={onClose}
              >
                {t("global.cancel") || "Annuler"}
              </button>
              {shipments.length === 1 ? (
                <button
                  className="px-4 py-2 rounded-full bg-primary text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => onSave(selected, modalAddress)}
                  disabled={!currentSelected}
                >
                  {t("global.save") || "Enregistrer"}
                </button>
              ) : (
                <>
                  {step < shipments.length - 1 && (
                    <button
                      className="px-4 py-2 rounded-full bg-primary text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() =>
                        setStep((s) => Math.min(s + 1, shipments.length - 1))
                      }
                      disabled={!currentSelected}
                    >
                      {t("global.next") || "Suivant"}
                    </button>
                  )}
                  {step === shipments.length - 1 && (
                    <button
                      className="px-4 py-2 rounded-full bg-primary text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => onSave(selected, modalAddress)}
                      disabled={shipments.some((s) => !selected[s.id])}
                    >
                      {t("global.save") || "Enregistrer"}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const CartPageShop = () => {
  const { cart, handleRemoveFromCart, setUserId } = useCart();
  const { t, i18n } = useTranslation();
  const [userId, setUserIdLocal] = useState(null);
  const { currentUser } = useAuth();
  const lang = i18n.language;
  const navigate = useNavigate();

  useEffect(() => {
    console.log("CURRENTUSER", currentUser);
    if (currentUser?.id) {
      setUserId(currentUser.id);
      setUserIdLocal(currentUser.id);
    }
  }, [currentUser, setUserId]);

  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [deliveryAddressObj, setDeliveryAddressObj] = useState(null);
  const [shippingModalOpen, setShippingModalOpen] = useState(false);
  const [selectedRates, setSelectedRates] = useState({});
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  const parseQty = (q) => (Number.isFinite(q) && q > 0 ? q : 1);
  const parsePrice = (p) => (Number.isFinite(p) && p >= 0 ? p : 0);

  const lineTotal = (item) =>
    Number.isFinite(item?.totalPrice)
      ? item.totalPrice
      : parsePrice(item?.realPrice ?? item?.price) * parseQty(item?.quantity);

  const payableCart = useMemo(
    () => cart.filter((it) => !isPendingItem(it)),
    [cart]
  );

  const pendingCart = useMemo(() => cart.filter(isPendingItem), [cart]);

  const hasShippableItems = useMemo(
    () => payableCart.some(isShippableItem),
    [payableCart]
  );

  const shipments = useMemo(
    () => groupCartIntoShipments(payableCart),
    [payableCart]
  );

  const subtotal = useMemo(
    () => payableCart.reduce((sum, it) => sum + lineTotal(it), 0),
    [payableCart]
  );

  const shippingTotal = useMemo(
    () =>
      hasShippableItems
        ? Object.values(selectedRates).reduce(
            (sum, r) => sum + (r?.price || 0),
            0
          )
        : 0,
    [selectedRates, hasShippableItems]
  );

  const taxRate = 0.14975;
  const taxableAmount = subtotal + shippingTotal;
  const taxes = useMemo(() => taxableAmount * taxRate, [taxableAmount]);
  const total = useMemo(() => taxableAmount + taxes, [taxableAmount, taxes]);

  const allShipmentsSelected =
    !hasShippableItems ||
    (shipments.length > 0 && shipments.every((s) => selectedRates[s.id]));

  const renderOptions = (item) => {
    const options = [];
    if (item.OptionName) options.push(item.OptionName);
    if (item.MaterialName) options.push(item.MaterialName);
    if (item.ColorName) options.push(item.ColorName);
    if (item.SizeName) options.push(item.SizeName);
    if (Array.isArray(item.OtherOptions))
      options.push(...item.OtherOptions.filter(Boolean));
    return options.length ? (
      <ul className="mt-1 text-sm text-gray-500 list-inside">
        {options.map((opt, idx) => (
          <li key={idx}>{opt}</li>
        ))}
      </ul>
    ) : null;
  };

  // ✅ FONCTION CORRIGÉE : construit la structure exacte attendue par l'API
  const buildShippingServiceCode = () => {
    // Si un seul shipment, renvoie directement le serviceCode
    if (shipments.length === 1) {
      const rate = selectedRates[shipments[0].id];
      return rate?.serviceCode || "";
    }

    // Si plusieurs shipments, tu peux :
    // Option A : Concaténer les codes (ex: "DOM.EP|DOM.XP")
    // Option B : Prendre le premier
    // Option C : Créer un objet JSON stringifié

    // Je recommande Option A pour simplicité :
    const codes = shipments
      .map((s) => selectedRates[s.id]?.serviceCode)
      .filter(Boolean)
      .join("|");

    return codes;
  };

  const handlePayNow = async () => {
    try {
      const API = process.env.REACT_APP_API_BASE_URL;

      // ✅ Validation avant l'envoi
      if (!userId) {
        alert("Vous devez être connecté pour effectuer un paiement.");
        return;
      }

      if (hasShippableItems && !allShipmentsSelected) {
        alert(
          "Veuillez sélectionner une option de livraison pour tous les envois."
        );
        return;
      }

      const shippingCode = buildShippingServiceCode();
      if (hasShippableItems && !shippingCode) {
        alert("Aucun service de livraison sélectionné.");
        return;
      }

      // ✅ Construction du payload avec la structure exacte attendue par l'API
      const payload = {
        userId: userId,
        shippingServiceCode: shippingCode || "NO_SHIPPING",
        shippingAddress:
          deliveryAddressObj?.formattedAddress || deliveryAddress || "",
      };

      console.log("📦 Envoi du payload:", payload);

      // ✅ Activer l'indicateur de chargement
      setIsProcessingPayment(true);

      const res = await fetch(`${API}/api/Store/MakePaymentIntent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorText = await res.text();
        console.error("❌ Erreur API:", errorText);
        throw new Error(`Erreur ${res.status}: ${errorText}`);
      }

      const data = await res.json();
      console.log("✅ Payment intent créé:", data);

      // ✅ REDIRECTION VERS STRIPE CHECKOUT
      if (data.checkoutUrl) {
        console.log("🔗 Redirection vers Stripe:", data.checkoutUrl);
        window.location.href = data.checkoutUrl;
      } else if (data.sessionId) {
        // Fallback si checkoutUrl n'est pas présent
        console.log("🔗 Redirection avec sessionId:", data.sessionId);
        window.location.href = data.checkoutUrl;
      } else {
        throw new Error("Aucune URL de paiement reçue du serveur");
      }
    } catch (err) {
      console.error("❌ Erreur paiement:", err);
      alert("Erreur lors de la création du paiement: " + err.message);
      setIsProcessingPayment(false); // Désactiver le chargement en cas d'erreur
    }
  };

  return (
    <div className="container mx-auto px-6 py-6 text-gray-600">
      <button
        onClick={() => navigate(-1)}
        className="btn bg-primary text-white rounded-full flex items-center gap-2 mb-4"
      >
        <ArrowLeftIcon className="w-5 h-5" />
        {t("global.back")}
      </button>

      <h1 className="text-2xl font-bold mb-6">{t("cart.cartTitle")}</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          {cart.length > 0 ? (
            cart.map((item, index) => (
              <div
                key={index}
                className="flex items-center justify-between pr-4 py-4 border rounded-lg shadow-sm bg-white"
              >
                <div className="flex items-start gap-4 pl-0">
                  <img
                    src={item.imageUrl || imagenotfound}
                    alt={item.title || item.nameFr || item.nameEn}
                    className="w-16 h-16 object-cover rounded"
                    loading="lazy"
                  />
                  <div>
                    <div className="font-bold text-md">
                      {item.title ||
                        (lang === "fr"
                          ? item.nameFr || item.nameEn
                          : item.nameEn || item.nameFr)}
                    </div>
                    {renderOptions(item)}
                    <div className="text-sm mt-1">
                      {t("cart.unitPrice")} :{" "}
                      {currency.format(
                        parsePrice(item.realPrice ?? item.price)
                      )}
                    </div>
                    <div className="text-sm">
                      {t("cart.quantity")} : {parseQty(item.quantity)}
                    </div>
                    {!isShippableItem(item) && (
                      <div className="text-xs mt-1 text-gray-500">
                        {t("cart.serviceNoShipping") ||
                          "Service (sans livraison)"}
                      </div>
                    )}
                    {isPendingItem(item) && (
                      <div className="text-[11px] inline-block mt-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300">
                        {t("cart.pendingBadge", "À confirmer (non facturé)")}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="font-semibold">
                    {currency.format(lineTotal(item))}
                  </div>
                  <button
                    onClick={() =>
                      handleRemoveFromCart(cart.findIndex((x) => x === item))
                    }
                    className="text-gray-400 hover:text-red-500"
                    aria-label={t("cart.removeItem")}
                    title={t("cart.removeItem")}
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))
          ) : (
            <p>{t("cart.noProductsInCart")}</p>
          )}
        </div>

        <div className="bg-gray-100 p-6 rounded-lg shadow-md space-y-6">
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span>{t("cart.subTotal")}</span>
              <span>{currency.format(subtotal)}</span>
            </div>
            <div className="flex justify-between">
              <span>{t("cart.shipping") || "Livraison"}</span>
              <span>
                {currency.format(
                  Object.values(selectedRates).reduce(
                    (s, r) => s + (r?.price || 0),
                    0
                  )
                )}
              </span>
            </div>
            <div className="flex justify-between">
              <span>{t("cart.taxes")}</span>
              <span>{currency.format(taxes)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold pt-2">
              <span>{t("cart.totalPrice")}</span>
              <span>{currency.format(total)}</span>
            </div>
          </div>

          {(() => {
            const hasShippable = payableCart.some(isShippableItem);
            if (!hasShippable) return null;
            return (
              <>
                <div>
                  <label className="block font-semibold mb-2">
                    {t("cart.deliveryAddress")}
                  </label>
                  <PlacesAutocomplete
                    initialAddress={deliveryAddress}
                    onSelect={(coords) => {
                      const text = coords?.formattedAddress || "";
                      setDeliveryAddress(text);
                      setDeliveryAddressObj(coords);
                      setSelectedRates({});
                    }}
                    showOptional={false}
                    title={t("cart.deliveryAddress")}
                    placeholder={
                      t("cart.enterAddress") || "Entrez une adresse au Québec"
                    }
                    customClassName=""
                    canEdit={true}
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    className="flex-1 bg-primary text-white px-4 py-2 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => setShippingModalOpen(true)}
                    disabled={
                      !deliveryAddressObj?.formattedAddress ||
                      shipments.length === 0
                    }
                    title={
                      !deliveryAddressObj?.formattedAddress
                        ? t("cart.pleaseEnterAddress") ||
                          "Veuillez entrer une adresse."
                        : undefined
                    }
                  >
                    {Object.keys(selectedRates).length > 0
                      ? t("cart.editShipping") || "Modifier la livraison"
                      : t("cart.chooseShipping") || "Choisir la livraison"}
                  </button>
                </div>
              </>
            );
          })()}

          <button
            className="w-full bg-gray-800 text-white px-4 py-2 rounded-full hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handlePayNow}
            disabled={
              cart.length === 0 ||
              !userId ||
              !(
                !payableCart.some(isShippableItem) ||
                (shipments.length > 0 &&
                  shipments.every((s) => selectedRates[s.id]))
              )
            }
            title={
              cart.length === 0
                ? t("cart.noProductsInCart")
                : !userId
                ? "Utilisateur non connecté"
                : !(
                    !payableCart.some(isShippableItem) ||
                    (shipments.length > 0 &&
                      shipments.every((s) => selectedRates[s.id]))
                  )
                ? t("cart.selectShippingForAll") ||
                  "Sélectionnez une option de livraison pour chaque envoi."
                : undefined
            }
          >
            {t("cart.payOnline")}
          </button>
        </div>
      </div>

      {payableCart.some(isShippableItem) && (
        <ShippingOptionsModal
          open={shippingModalOpen}
          onClose={() => setShippingModalOpen(false)}
          shipments={shipments}
          addressObj={deliveryAddressObj}
          initialSelected={selectedRates}
          cart={payableCart}
          onSave={(selectedMap, updatedAddress) => {
            setSelectedRates(selectedMap);
            if (
              updatedAddress?.formattedAddress &&
              updatedAddress.formattedAddress !== deliveryAddress
            ) {
              setDeliveryAddress(updatedAddress.formattedAddress);
              setDeliveryAddressObj(updatedAddress);
            }
            setShippingModalOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default CartPageShop;
